// Flow 5 - MVP Build: cloud choice, per-step build progress, and the three hold states
// (docs/phase-0/slice5-ux-flows.md §8; route `/apps/new/build`). On completion, stage
// flips to "mvp" and the screen hands off to `/apps/:appId/explained` (mirrors
// `RouteCard.tsx`'s own confirm -> success-card -> Continue pattern).
//
// ADR-0022 (Slice 5 close): the build hold-RESOLUTION routes (`POST
// /build/:buildId/steps/:stepId/{confirm-hard-gate,verify-manual-step}`) are DEFERRED to
// Slice 18, coupled to real build execution - `verify-manual-step` is a
// forged-completion-evidence attack surface that must ship WITH its Slice-18 hardening,
// never as a throwaway stub ahead of it. The `awaiting_hard_gate`/`awaiting_manual_step`
// HOLD states below stay exactly as built (the safety property: a build correctly PAUSES
// on payments/auth/pii or on a manual step) - only the confirm/verify BUTTON is replaced
// with an honest "available with real build execution" affordance, never a button that
// calls a route that does not exist. The per-step-id heuristic this file used to carry
// (`BuildResult`'s hold members carry no `buildId`/step reference, model/build.ts:
// 147-148) is removed with the calls that needed it - nothing reads a step id anymore.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppView, BuildResult, Cloud } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { BuildCloudChoiceCard } from "../../components/BuildCloudChoiceCard.js";
import { BuildStepRow } from "../../components/BuildStepRow.js";
import { BuildTargetRefusalCard } from "../../components/BuildTargetRefusalCard.js";
import { GdprRoutingBanner } from "../../components/GdprRoutingPanel.js";
import { BuildConfirmDialog } from "../../components/BuildConfirmDialog.js";
import { startBuild, getBuildStatus } from "../../api/build.js";
import { getApp } from "../../api/apps.js";
import { buildCopy } from "../../copy/build.js";
import { isOwnerOrAdmin, isViewerRole, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";

const POLL_MS = 4000;

const HARD_GATE_REASON_KEY = {
  payments: "build.mvp.hardGate.reason.payments",
  auth: "build.mvp.hardGate.reason.auth",
  pii: "build.mvp.hardGate.reason.pii",
} as const;

type AppLoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "ready"; readonly app: AppView };

export const MvpBuild = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { draft } = useWizard();
  const navigate = useNavigate();
  const appId = draft.appId;

  const [appState, setAppState] = useState<AppLoadState>({ kind: "loading" });
  const [cloud, setCloud] = useState<Cloud | null>(null);
  const [result, setResult] = useState<BuildResult | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(false);
  const [hardGateOpen, setHardGateOpen] = useState(false);
  const hardGateTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    void (async () => {
      const appResult = await getApp(appId);
      if (cancelled.current) return;
      setAppState(isErr(appResult) ? { kind: "error" } : { kind: "ready", app: appResult.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  // Poll while a build is genuinely in progress.
  useEffect(() => {
    if (appId === null || result?.state !== "building") return undefined;
    const buildId = result.buildId;
    const timer = window.setInterval(() => {
      void (async () => {
        const polled = await getBuildStatus(appId, buildId);
        if (!isErr(polled)) setResult(polled.value);
      })();
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [appId, result]);

  if (appId === null || appState.kind === "loading") {
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.titleGeneric")}>
        <div className="card pad skeleton-card" />
      </FocusedFlowShell>
    );
  }

  if (appState.kind === "error") {
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.titleGeneric")}>
        <div className="card state-card">
          <p>We couldn&apos;t load this app. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAppState({ kind: "loading" });
            }}
          >
            Retry
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { app } = appState;
  const viewer = isViewerRole(session);
  const canAct = isOwnerOrAdmin(session);
  const viewerReason = viewer ? buildCopy("build.mvp.deniedRole") : undefined;

  const runStart = (chosenCloud: Cloud): void => {
    setStarting(true);
    setStartError(false);
    void (async () => {
      const startResult = await startBuild(app.id, "mvp", chosenCloud);
      setStarting(false);
      if (isErr(startResult)) {
        setStartError(true);
        return;
      }
      setResult(startResult.value);
    })();
  };

  // ---- Cloud choice (not yet started) ----
  if (result === null) {
    return (
      <FocusedFlowShell
        title={buildCopy("build.mvp.titleGeneric")}
        onBack={() => {
          void navigate("/apps/new/build/workflow");
        }}
        cta={
          <button
            type="button"
            className="btn btn-primary btn-lg"
            disabled={cloud === null || starting || viewer}
            onClick={() => {
              if (cloud !== null) runStart(cloud);
            }}
          >
            {starting ? "Starting..." : buildCopy("build.mvp.cloud.confirmCta", { appName: app.name })}
          </button>
        }
      >
        <h1 className="h2">{buildCopy("build.mvp.cloud.prompt", { appName: app.name })}</h1>
        {startError ? (
          <p className="field-error mt8" role="alert">
            {buildCopy("build.mvp.error")}
          </p>
        ) : null}
        {/* Responsive, stacks to one column on phone (design-review W3) - auto-fit/
            minmax reflows itself with no breakpoint, same pattern BrandDirectionPicker's
            own gallery grid already uses (BrandDirectionPicker.tsx:208). */}
        <div className="grid mt16" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          <BuildCloudChoiceCard
            title={buildCopy("build.mvp.cloud.managed.name")}
            description={buildCopy("build.mvp.cloud.managed.description")}
            selected={cloud === "supabase"}
            onSelect={() => {
              setCloud("supabase");
            }}
            {...(viewerReason !== undefined ? { disabledReason: viewerReason } : {})}
          />
          <BuildCloudChoiceCard
            title={buildCopy("build.mvp.cloud.production.name")}
            description={buildCopy("build.mvp.cloud.production.description")}
            selected={cloud !== null && cloud !== "supabase"}
            onSelect={() => {
              setCloud(app.cloud ?? "aws");
            }}
            {...(viewerReason !== undefined ? { disabledReason: viewerReason } : {})}
          />
        </div>
      </FocusedFlowShell>
    );
  }

  // ---- refused_entitlement (D65) ----
  if (result.state === "refused_entitlement") {
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.title", { appName: app.name })}>
        <BuildTargetRefusalCard
          requiredTier={result.requiredTier}
          onAskAboutUpgrading={() => undefined}
          onBackToApps={() => {
            void navigate("/apps");
          }}
        />
      </FocusedFlowShell>
    );
  }

  // ---- awaiting_hard_gate (top-level - D46/D47/D50 the founder's absolute gate) ----
  if (result.state === "awaiting_hard_gate") {
    const reasonText = buildCopy(HARD_GATE_REASON_KEY[result.reason]);
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.title", { appName: app.name })}>
        <h1 className="h2">{buildCopy("build.mvp.title", { appName: app.name })}</h1>
        <GdprRoutingBanner
          count={1}
          canAct={canAct}
          onOpen={() => {
            setHardGateOpen(true);
          }}
        />
        {hardGateOpen ? (
          <BuildConfirmDialog
            heading={buildCopy("build.mvp.hardGate.banner")}
            body={`${buildCopy("build.mvp.hardGate.panelWhy", { step: app.name, reason: reasonText })} ${buildCopy("build.mvp.hardGate.resolutionDeferred")}`}
            confirmLabel={buildCopy("build.mvp.hardGate.acknowledgeCta")}
            cancelLabel={buildCopy("build.mvp.hardGate.notNowCta")}
            triggerRef={hardGateTriggerRef}
            onConfirm={() => {
              setHardGateOpen(false);
            }}
            onCancel={() => {
              setHardGateOpen(false);
            }}
          />
        ) : null}
      </FocusedFlowShell>
    );
  }

  // ---- awaiting_manual_step (top-level - D78 repo creation) ----
  if (result.state === "awaiting_manual_step") {
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.title", { appName: app.name })}>
        <h1 className="h2">{buildCopy("build.mvp.title", { appName: app.name })}</h1>
        <div
          className="well pad manual-step-card"
          role="region"
          aria-label={buildCopy("build.mvp.manualStep.heading")}
          data-testid="manual-step-card"
        >
          <h2 className="h3">{buildCopy("build.mvp.manualStep.heading")}</h2>
          <p className="muted mt8" style={{ fontSize: 13.5 }}>
            {buildCopy("build.mvp.manualStep.body", { appName: app.name })}
          </p>
          <a className="btn btn-ghost mt12" href="https://github.com/new" target="_blank" rel="noreferrer">
            {buildCopy("build.mvp.manualStep.linkCta")}
          </a>
          <p className="muted deferred-note" style={{ fontSize: 13 }}>
            {buildCopy("build.mvp.manualStep.resolutionDeferred")}
          </p>
        </div>
      </FocusedFlowShell>
    );
  }

  // ---- built (complete) ----
  if (result.state === "built") {
    return (
      <FocusedFlowShell title={buildCopy("build.mvp.title", { appName: app.name })}>
        <div className="card pad" role="status">
          <p>{buildCopy("build.mvp.complete.body", { appName: app.name })}</p>
          <span
            className="chip mt8"
            style={{ borderColor: "var(--amber-line)", background: "var(--amber-soft)" }}
          >
            {buildCopy("build.mvp.complete.certifyCallout")}
          </span>
          <button
            type="button"
            className="btn btn-primary mt12"
            onClick={() => {
              void navigate(`/apps/${app.id}/explained`);
            }}
          >
            {buildCopy("build.mvp.complete.continueCta")}
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  // ---- building (the ordered per-step progress list) ----
  const runningStep = result.steps.find((step) => step.state === "running");
  return (
    <FocusedFlowShell title={buildCopy("build.mvp.title", { appName: app.name })}>
      <h1 className="h2">{buildCopy("build.mvp.building", { appName: app.name })}</h1>
      <p className="muted mt8" role="status">
        {runningStep !== undefined
          ? buildCopy("build.mvp.statusReadout", { appName: app.name, stepLabel: runningStep.label })
          : buildCopy("build.mvp.building", { appName: app.name })}
      </p>
      <ol className="build-step-list mt16" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {result.steps.map((step) => (
          <BuildStepRow
            key={step.id}
            step={step}
            {...(step.state === "failed"
              ? {
                  onRetry: () => {
                    runStart(cloud ?? "supabase");
                  },
                }
              : {})}
          />
        ))}
      </ol>
    </FocusedFlowShell>
  );
};
